'use client';

import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { useDebounce } from '@uidotdev/usehooks';
import { useEffect, useMemo, useState } from 'react';

import { useListDemandExamplesQuery } from '../../services/api/demandExampleService';
import { MatterType } from '../../services/types/client-intake-types';
import {
  DemandExample,
  DemandExampleFilters,
} from '../../services/types/demand-example-types';
import { Badge } from '../ui/catalyst/badge';
import { Button } from '../ui/catalyst/button';
import { Input } from '../ui/catalyst/input';
import { Select } from '../ui/catalyst/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/catalyst/table';
import { Text } from '../ui/catalyst/text';
import { Tooltip, TooltipContent, TooltipTrigger } from '../ui/tooltip';

interface DemandExampleSelectorProps {
  onSelect?: (demandId: string) => void;
  selectedId?: string;
  className?: string;
  allowMultiSelect?: boolean;
  defaultMultiSelect?: boolean;
  onSelectMany?: (demandIds: string[]) => void;
  selectedIds?: string[];
}

export const DemandExampleSelector = ({
  onSelect,
  selectedId,
  className = '',
  allowMultiSelect = false,
  defaultMultiSelect = false,
  onSelectMany,
  selectedIds,
}: DemandExampleSelectorProps) => {
  // State for filters and search

  const [filters, setFilters] = useState<Partial<DemandExampleFilters>>({
    ordering: '-created_at',
    limit: 25,
    offset: 0,
  });
  const debouncedFilters = useDebounce(filters, 300);

  // Multi-select state
  const [isMultiSelect, setIsMultiSelect] = useState<boolean>(
    allowMultiSelect ? defaultMultiSelect : false,
  );
  const [selectedMany, setSelectedMany] = useState<string[]>(selectedIds || []);

  useEffect(() => {
    if (Array.isArray(selectedIds)) {
      setSelectedMany(selectedIds);
    }
  }, [selectedIds]);

  // RTK Query hook
  const {
    data: demandExamples,
    isLoading,
    isFetching,
    error,
  } = useListDemandExamplesQuery(debouncedFilters, {
    refetchOnMountOrArgChange: true,
  });

  // Handlers
  const handleSearch = (search: string) => {
    setFilters((prev) => ({ ...prev, search, offset: 0 }));
  };

  const handleFilterChange = (
    key: keyof DemandExampleFilters,
    value: unknown,
  ) => {
    setFilters((prev) => ({ ...prev, [key]: value, offset: 0 }));
  };

  const handleRowClick = (example: DemandExample) => {
    if (isMultiSelect) {
      setSelectedMany((prev) => {
        const exists = prev.includes(example.id);
        if (exists) {
          return prev.filter((id) => id !== example.id);
        }
        return [...prev, example.id];
      });
      return;
    }
    onSelect?.(example.id);
  };

  const handleToggleAllCurrentPage = () => {
    if (!demandExamples?.results) return;
    const pageIds = demandExamples.results.map((e) => e.id);
    const allSelected = pageIds.every((id) => selectedMany.includes(id));
    if (allSelected) {
      setSelectedMany((prev) => prev.filter((id) => !pageIds.includes(id)));
      return;
    }
    setSelectedMany((prev) => Array.from(new Set([...prev, ...pageIds])));
  };

  const handleConfirmSelection = () => {
    if (!isMultiSelect) return;
    if (!onSelectMany) return;
    onSelectMany(selectedMany);
  };

  const handlePageChange = (page: number) => {
    const offset = (page - 1) * (filters.limit || 25);
    setFilters((prev) => ({ ...prev, offset }));
  };

  // Extract unique values from the data for filter options
  const filterOptions = useMemo(() => {
    if (!demandExamples?.results) {
      return { tags: [], jurisdictions: [], firms: [] };
    }

    const allTags = new Set<string>();
    const allJurisdictions = new Set<string>();
    const allFirms = new Set<{ id: string; name: string }>();

    demandExamples.results.forEach((example) => {
      example.tags?.forEach((tag) => allTags.add(tag));
      example.jurisdiction?.forEach((jurisdiction) =>
        allJurisdictions.add(jurisdiction),
      );
      if (example.firm_details) {
        allFirms.add({
          id: example.firm_details.id,
          name: example.firm_details.name,
        });
      }
    });

    return {
      tags: Array.from(allTags).sort(),
      jurisdictions: Array.from(allJurisdictions).sort(),
      firms: Array.from(allFirms, (firm) => firm).sort((a, b) =>
        a.name.localeCompare(b.name),
      ),
    };
  }, [demandExamples]);

  // Check if any filters are active
  const hasActiveFilters = !!(
    filters.matter_type ||
    filters.firm ||
    filters.jurisdiction
  );

  // Computed values
  const currentPage =
    Math.floor((filters.offset || 0) / (filters.limit || 25)) + 1;
  const totalPages = demandExamples
    ? Math.ceil(demandExamples.count / (filters.limit || 25))
    : 0;

  if (error) {
    return (
      <div
        className={`bg-white rounded-lg border border-zinc-200 ${className}`}
      >
        <div className="p-6 text-center">
          <Text className="text-red-600">Error loading demand examples</Text>
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-white rounded-lg border border-zinc-200 ${className}`}>
      {/* Search and Filters Header */}
      <div className="p-4 border-b border-zinc-200">
        <div className="flex items-center gap-3">
          {/* Full-width search bar */}
          <div className="flex-1 relative">
            <div className="relative">
              <Input
                placeholder="Search examples by title, description, tags, firm..."
                value={filters.search || ''}
                onChange={(e) => handleSearch(e.target.value)}
                className="w-full text-base pl-10 border-zinc-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 [&>input]:border-none"
              />
              <div className="absolute left-3 top-1/2 transform -translate-y-1/2">
                {isLoading || isFetching ? (
                  <div className="animate-spin h-4 w-4 border-2 border-zinc-300 border-t-zinc-600 rounded-full" />
                ) : (
                  <MagnifyingGlassIcon className="h-4 w-4 text-zinc-400" />
                )}
              </div>
            </div>
          </div>

          {/* Filters - Compact layout on the right */}
          <div className="flex items-center gap-3">
            <span className="text-sm font-medium text-zinc-700 whitespace-nowrap">
              Filter by:
            </span>

            <Select
              value={filters.matter_type || ''}
              onChange={(e) =>
                handleFilterChange('matter_type', e.target.value)
              }
              className="min-w-[140px]"
              id="filter-matter-type"
            >
              <option value="">All Matter Types</option>
              {Object.values(MatterType).map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </Select>

            <Select
              value={filters.firm || ''}
              onChange={(e) => handleFilterChange('firm', e.target.value)}
              className="min-w-[130px]"
              disabled={!demandExamples}
              id="filter-firm"
            >
              <option value="">All Firms</option>
              {filterOptions.firms.map((firm) => (
                <option key={firm.id} value={firm.id}>
                  {firm.name}
                </option>
              ))}
            </Select>

            <Select
              value={filters.jurisdiction || ''}
              onChange={(e) =>
                handleFilterChange('jurisdiction', e.target.value)
              }
              className="min-w-[130px]"
              disabled={!demandExamples}
              id="filter-jurisdiction"
            >
              <option value="">All Jurisdictions</option>
              {filterOptions.jurisdictions.map((jurisdiction) => (
                <option key={jurisdiction} value={jurisdiction}>
                  {jurisdiction}
                </option>
              ))}
            </Select>

            <Select
              value={filters.ordering || '-created_at'}
              onChange={(e) => handleFilterChange('ordering', e.target.value)}
              className="min-w-[130px]"
              id="filter-ordering"
            >
              <option value="-created_at">Newest First</option>
              <option value="created_at">Oldest First</option>
              <option value="title">Title A-Z</option>
              <option value="-title">Title Z-A</option>
            </Select>

            {allowMultiSelect && (
              <div className="flex items-center gap-2 ml-2">
                <label
                  className="flex items-center gap-2"
                  htmlFor="selector-multi-select"
                >
                  <input
                    id="selector-multi-select"
                    type="checkbox"
                    checked={isMultiSelect}
                    onChange={(e) => setIsMultiSelect(e.target.checked)}
                  />
                  <span className="text-sm">Multi-select</span>
                </label>
                {isMultiSelect && (
                  <>
                    <Button
                      onClick={handleConfirmSelection}
                      disabled={selectedMany.length === 0 || !onSelectMany}
                      className="text-sm"
                    >
                      Confirm ({selectedMany.length})
                    </Button>
                    <Button
                      plain
                      onClick={() => setSelectedMany([])}
                      disabled={selectedMany.length === 0}
                      className="text-sm"
                    >
                      Clear
                    </Button>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-hidden">
        {isLoading && !demandExamples ? (
          <div className="p-6 text-center">
            <Text>Loading demand examples...</Text>
          </div>
        ) : (
          <Table>
            <TableHead>
              <TableRow>
                {isMultiSelect && (
                  <TableHeader className="w-10">
                    <input
                      type="checkbox"
                      aria-label="Select all on page"
                      checked={
                        !!demandExamples?.results.length &&
                        demandExamples.results.every((e) =>
                          selectedMany.includes(e.id),
                        )
                      }
                      onChange={handleToggleAllCurrentPage}
                    />
                  </TableHeader>
                )}
                <TableHeader>
                  <div className="ml-3">Title</div>
                </TableHeader>
                <TableHeader>Matter Type</TableHeader>
                <TableHeader>Firm</TableHeader>
                <TableHeader>Tags</TableHeader>
                <TableHeader>File</TableHeader>
              </TableRow>
            </TableHead>
            <TableBody>
              {demandExamples?.results.map((example) => (
                <TableRow
                  key={example.id}
                  className={`cursor-pointer hover:bg-blue-50 transition-colors ${
                    isMultiSelect
                      ? selectedMany.includes(example.id)
                        ? 'bg-blue-100 border-blue-200'
                        : ''
                      : selectedId === example.id
                        ? 'bg-blue-100 border-blue-200'
                        : ''
                  }`}
                  onClick={() => handleRowClick(example)}
                  aria-selected={
                    isMultiSelect
                      ? selectedMany.includes(example.id)
                      : selectedId === example.id
                  }
                >
                  {isMultiSelect && (
                    <TableCell className="w-10">
                      <input
                        type="checkbox"
                        checked={selectedMany.includes(example.id)}
                        onChange={() => handleRowClick(example)}
                        onClick={(e) => e.stopPropagation()}
                        aria-label={`Select ${example.title || 'Untitled'}`}
                        className="h-4 w-4"
                      />
                    </TableCell>
                  )}
                  <TableCell>
                    <div>
                      {example.description ? (
                        <Tooltip>
                          <TooltipTrigger>
                            <span className="font-medium ml-3 cursor-help text-zinc-900 truncate flex">
                              {example.title || 'Untitled'}
                            </span>
                          </TooltipTrigger>
                          <TooltipContent className="max-w-xs">
                            {example.description}
                          </TooltipContent>
                        </Tooltip>
                      ) : (
                        <Text className="font-medium ml-3">
                          {example.title || 'Untitled'}
                        </Text>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    {example.matter_type && (
                      <Badge color="blue">{example.matter_type}</Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    <Text className="text-sm">
                      {example.firm_details?.name || 'No Firm'}
                    </Text>
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {example.tags?.slice(0, 2).map((tag, index) => (
                        <Badge key={index} color="zinc" className="text-xs">
                          {tag}
                        </Badge>
                      ))}
                      {(example.tags?.length || 0) > 2 && (
                        <Tooltip>
                          <TooltipTrigger>
                            <span className="inline-flex items-center rounded-md bg-zinc-50 px-2 py-1 text-xs font-medium text-zinc-600 ring-1 ring-inset ring-zinc-500/10 cursor-help">
                              +{(example.tags?.length || 0) - 2}
                            </span>
                          </TooltipTrigger>
                          <TooltipContent className="max-w-xs">
                            <div className="space-y-1">
                              <div className="text-xs font-medium">
                                Additional tags:
                              </div>
                              <div className="flex flex-wrap gap-1">
                                {example.tags?.slice(2).map((tag, index) => (
                                  <span
                                    key={index}
                                    className="text-xs px-1.5 py-0.5 rounded"
                                  >
                                    {tag}
                                  </span>
                                ))}
                              </div>
                            </div>
                          </TooltipContent>
                        </Tooltip>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="mr-3">
                      {example.demand ? (
                        <Badge
                          color="green"
                          className="flex items-center gap-1 w-fit"
                        >
                          File
                        </Badge>
                      ) : (
                        <Text className="text-zinc-400 text-sm">No file</Text>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}

        {/* Empty state */}
        {!isLoading &&
          demandExamples &&
          demandExamples.results.length === 0 && (
            <div className="p-6 text-center">
              <Text className="text-zinc-500">
                {filters.search || hasActiveFilters
                  ? 'No examples match your search criteria'
                  : 'No demand examples found'}
              </Text>
            </div>
          )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="p-4 border-t border-zinc-200">
          <div className="flex justify-between items-center">
            <Button
              plain
              disabled={currentPage === 1}
              onClick={() => handlePageChange(currentPage - 1)}
              className="text-sm"
            >
              Previous
            </Button>
            <span className="text-sm text-zinc-600">
              Page {currentPage} of {totalPages} • {demandExamples?.count} total
            </span>
            <Button
              plain
              disabled={currentPage === totalPages}
              onClick={() => handlePageChange(currentPage + 1)}
              className="text-sm"
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};
